/**
 * @biomax/plugin-economic — burden + cost-benefit accounting.
 *
 * Reference frameworks: GBD (Global Burden of Disease) methodology
 * for DALYs / YLL / YLD; WHO-CHOICE for cost-effectiveness ratios.
 *
 * v0.1 ships:
 *   - YLL: Years of Life Lost = Σ deaths × (life-expectancy at age)
 *   - YLD: Years Lived with Disability = Σ cases × disability-weight × duration
 *   - DALY = YLL + YLD
 *   - QALY: Quality-Adjusted Life Years
 *   - Cost-per-DALY-averted comparison of intervention vs counterfactual
 *
 * v0.4+ adds region-specific life-expectancy tables + GDP-impact
 * functions per intervention class.
 */

import type { PluginManifest } from '@biomax/types';

/** Standard reference life expectancy at age (Global Burden of Disease 2019 std). */
export const GBD_REF_LIFE_EXPECTANCY = [
  88.871, 88.701, 87.929, 86.943, 85.951, 84.953, 83.957, 82.961, 81.964, 80.965,
  79.967, 78.970, 77.974, 76.979, 75.983, 74.988, 73.994, 73.000, 72.007, 71.014,
  70.021, 69.029, 68.037, 67.046, 66.055, 65.064, 64.073, 63.082, 62.091, 61.100,
  60.109, 59.118, 58.127, 57.137, 56.146, 55.156, 54.166, 53.176, 52.187, 51.198,
  50.211, 49.224, 48.238, 47.253, 46.270, 45.288, 44.308, 43.328, 42.349, 41.371,
  40.395, 39.420, 38.446, 37.474, 36.503, 35.533, 34.564, 33.597, 32.631, 31.667,
  30.704, 29.743, 28.783, 27.825, 26.868, 25.913, 24.959, 24.007, 23.056, 22.107,
  21.160, 20.214, 19.271, 18.331, 17.394, 16.461, 15.532, 14.609, 13.692, 12.781,
  11.879, 10.985, 10.103, 9.232, 8.376, 7.535, 6.711, 5.907, 5.123, 4.359,
  3.610,
];

export function lifeExpectancyAtAge(age: number): number {
  const i = Math.max(0, Math.min(GBD_REF_LIFE_EXPECTANCY.length - 1, Math.floor(age)));
  return GBD_REF_LIFE_EXPECTANCY[i]!;
}

/** Years of Life Lost from a death cohort. */
export interface DeathCohort {
  ageAtDeath: number;
  count: number;
}

export function yearsOfLifeLost(deaths: DeathCohort[]): number {
  let yll = 0;
  for (const d of deaths) yll += d.count * lifeExpectancyAtAge(d.ageAtDeath);
  return yll;
}

/** Years Lived with Disability for a case cohort with disability weight. */
export interface DisabilityCohort {
  caseCount: number;
  /** Disability weight in [0, 1]. 0 = no disability; 1 = death-equivalent. */
  disabilityWeight: number;
  /** Duration of disability, years. */
  durationYears: number;
}

export function yearsLivedWithDisability(cases: DisabilityCohort[]): number {
  let yld = 0;
  for (const c of cases) yld += c.caseCount * c.disabilityWeight * c.durationYears;
  return yld;
}

export function disabilityAdjustedLifeYears(
  deaths: DeathCohort[], cases: DisabilityCohort[],
): number {
  return yearsOfLifeLost(deaths) + yearsLivedWithDisability(cases);
}

/** Standard severity-class disability weights from GBD 2019. */
export const DISABILITY_WEIGHTS = {
  acuteRespiratoryMild: 0.006,
  acuteRespiratoryModerate: 0.053,
  acuteRespiratorySevere: 0.210,
  hospitalisationGeneralWard: 0.123,
  hospitalisationICU: 0.655,
  longSequelaeMild: 0.054,
  longSequelaeModerate: 0.180,
  longSequelaeSevere: 0.408,
  measlesAcute: 0.130,
  pertussisInfantSevere: 0.235,
  dengueSevere: 0.210,
  ebolaAcute: 0.655,
} as const;

export interface InterventionCostBenefit {
  /** Intervention name. */
  intervention: string;
  /** DALYs averted vs the no-intervention counterfactual. */
  dalysAverted: number;
  /** Total intervention cost (USD). */
  costUsd: number;
  /** Cost per DALY averted. */
  costPerDalyAverted: number;
  /** WHO-CHOICE threshold-comparable category. */
  category: 'highly-cost-effective' | 'cost-effective' | 'not-cost-effective';
}

/** Per WHO-CHOICE 2014 rules of thumb (per-capita GDP multipliers). */
export function classifyCostEffectiveness(
  costPerDaly: number, gdpPerCapita: number,
): InterventionCostBenefit['category'] {
  if (costPerDaly < gdpPerCapita) return 'highly-cost-effective';
  if (costPerDaly < gdpPerCapita * 3) return 'cost-effective';
  return 'not-cost-effective';
}

export function summariseCostBenefit(
  intervention: string,
  dalysAverted: number,
  costUsd: number,
  gdpPerCapita: number,
): InterventionCostBenefit {
  const costPer = dalysAverted > 0 ? costUsd / dalysAverted : Infinity;
  return {
    intervention,
    dalysAverted,
    costUsd,
    costPerDalyAverted: costPer,
    category: classifyCostEffectiveness(costPer, gdpPerCapita),
  };
}

/**
 * Citations for the load-bearing data in this plugin. PMIDs round-tripped
 * via @biomax/plugin-literature-fetch on the date in verifiedAt. Re-run
 * scripts/verify-references.ts to re-validate.
 */
export const ECONOMIC_REFERENCES = [
  {
    id: 'gbd-2019-life-exp',
    label: 'GBD 2019 life-expectancy systematic analysis — Lancet 2020',
    pmid: '33069325',
    doi: '10.1016/s0140-6736(20)30977-6',
    appliesTo: 'GBD_REF_LIFE_EXPECTANCY',
    note: 'Global age-sex-specific fertility, mortality, healthy life expectancy (HALE), and population estimates. GBD 2019 reference life-expectancy values used to weight YLL.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'gbd-disability-weights',
    label: 'Salomon et al. 2015 Lancet Global Health — GBD disability weights',
    pmid: '26475018',
    doi: '10.1016/s2214-109x(15)00069-8',
    appliesTo: 'DISABILITY_WEIGHTS',
    note: 'Salomon et al. "Disability weights for the GBD 2013 study". Many weights carry forward to GBD 2019; pathogen-specific weights (measlesAcute, dengueSevere, ebolaAcute) are conservative defaults pending region-specific catalog wiring in v0.3.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'gbd-2019-summary',
    label: 'GBD 2019 risk-factors systematic analysis — Lancet 2020',
    pmid: '33069327',
    doi: '10.1016/s0140-6736(20)30752-2',
    appliesTo: 'methodology',
    note: 'Companion GBD 2019 paper providing the DALY-decomposition + age-weighting methodology this plugin follows.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'who-choice-thresholds',
    label: 'WHO-CHOICE per-capita-GDP threshold rule of thumb',
    pmid: null,
    doi: null,
    appliesTo: 'classifyCostEffectiveness',
    note: 'No single canonical PMID; the 1× / 3× GDP-per-capita thresholds originated in WHO-CHOICE 2003+ documentation and were formally retired by WHO in 2014 in favour of opportunity-cost frameworks. v0.4 adds an alternate threshold module.',
    verifiedAt: null,
  },
] as const;

export const manifest: PluginManifest = {
  id: 'economic', version: '0.1.0',
  engineCompat: '^0.5.0', kingdoms: '*', systems: [], components: [],
};
